import { useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Section, SaveButton } from "@/components/CalcUI";
import { TallyHeader } from "@/components/tally/TallyHeader";
import { JointsEditor } from "@/components/tally/JointsEditor";
import { RowsEditor } from "@/components/tally/RowsEditor";
import { TallyResults } from "@/components/tally/TallyResults";
import { computeTally, newJoint } from "@/lib/tally";
import { downloadTallyPdf } from "@/lib/api";

const HEADER = {
  company: "", well_name: "", rig: "", field: "", api_no: "", date: new Date().toISOString().slice(0, 10),
  string_type: "drill", string_desc: "", string_od: "5", string_id: "4.276", offset: "0", td: "",
  casing_desc: "", casing_od: "9.625", casing_id: "8.681", shoe_md: "5000", open_hole: "8.5",
};
const FLUIDS = { mw_annulus: "10.0", mw_pipe: "10.0", slug_weight: "", slug_volume: "", tvd: "", pump_output: "0.1" };
const stripKey = ({ key, ...r }) => r;

function Block({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="mono text-[10px] tracking-[0.3em] text-[#f59e0b]">{title}</h3>
      {children}
    </div>
  );
}

function SummaryStrip({ s }) {
  const items = [
    ["Joints", s.joints, ""], ["Bit depth", s.bit_depth_ft?.toLocaleString(), "ft"], ["Annular", s.annular_volume_bbl, "bbl"],
    ["Hydrostatic", s.hydrostatic_annulus_psi?.toLocaleString(), "psi"], ["Differential", s.differential_psi?.toLocaleString(), "psi"],
  ];
  return (
    <div data-testid="tally-summary-strip" className="fixed bottom-0 left-0 right-0 lg:left-64 z-30 grid grid-cols-3 sm:grid-cols-5 gap-px bg-[#334155] border-t border-[#f59e0b]/60 shadow-[0_-10px_30px_rgba(0,0,0,0.5)]">
      {items.map(([label, v, u]) => (
        <div key={label} className="bg-[#0b0e13]/95 backdrop-blur px-3 py-2 last:col-span-2 sm:last:col-span-1">
          <div className="mono text-[9px] tracking-widest uppercase text-[#64748b]">{label}</div>
          <div className="mono text-sm text-[#fbbf24]">{v ?? "—"} <span className="text-[9px] text-[#64748b]">{u}</span></div>
        </div>
      ))}
    </div>
  );
}

export default function Tally() {
  const [header, setHeader] = useState(HEADER);
  const [fluids, setFluids] = useState(FLUIDS);
  const [joints, setJoints] = useState([]);
  const [extra, setExtra] = useState([]);
  const [pores, setPores] = useState([]);

  const sections = useMemo(() => {
    const total = joints.reduce((a, j) => a + (parseFloat(j.length) || 0), 0) + (parseFloat(header.offset) || 0);
    const td = Math.max(parseFloat(header.td) || 0, total, parseFloat(header.shoe_md) || 0);
    return [
      { name: `Casing ${header.casing_desc || header.casing_od + '"'}`.trim(), id: header.casing_id, bottom: header.shoe_md },
      ...extra,
      { name: "Open hole", id: header.open_hole, bottom: String(td) },
    ];
  }, [header, joints, extra]);

  const result = useMemo(
    () => computeTally({ joints, sections, fluids, porePoints: pores, offset: header.offset }),
    [joints, sections, fluids, pores, header.offset]
  );

  const payload = () => {
    if (!joints.length) return null;
    const title = [header.well_name, "Tally"].filter(Boolean).join(" — ");
    return {
      category: "Tally Sheet", calculator: "tally", units: "field", default_name: title || "Tally Sheet",
      inputs: { header, fluids, sections, pore_points: pores.map(stripKey), joints: joints.map(stripKey) },
      outputs: { summary: result.summary, holes: result.holes, overbalance: result.overbalance },
      share: {
        inputs: { company: header.company, well: header.well_name, rig: header.rig, string: header.string_desc },
        outputs: (({ bit_depth_ft, hydrostatic_annulus_psi, differential_psi, annular_volume_bbl, pipe_volume_bbl, strokes_bottoms_up, ...rest }) => ({
          bit_depth_ft, hydrostatic_annulus_psi, differential_psi, annular_volume_bbl, pipe_volume_bbl, strokes_bottoms_up, ...rest,
        }))(result.summary),
      },
    };
  };

  const pdf = async () => {
    const p = payload();
    if (!p) return toast.error("Add joints first");
    try {
      await downloadTallyPdf(p.default_name, p.inputs, p.outputs);
    } catch (e) {
      console.error("Tally PDF failed", e);
      toast.error("PDF failed — try again");
    }
  };

  return (
    <div data-testid="tally-page" className="pb-28">
      <Section title="Tally Sheet" subtitle="Pipe tally with running depth, volumes, annular capacities, hydrostatics and differential pressure · FIELD UNITS" testid="tally-section">
        <div className="space-y-8">
          <TallyHeader header={header} setHeader={setHeader} fluids={fluids} setFluids={setFluids} />
          <Block title="JOINTS">
            <JointsEditor joints={joints} setJoints={setJoints} header={header} running={result.rows} />
          </Block>
          <div className="grid gap-6 xl:grid-cols-2">
            <Block title="EXTRA HOLE SECTIONS (LINER, 2ND CASING…)">
              <RowsEditor testid="tally-section-row" rows={extra} setRows={setExtra} addLabel="Add section"
                newRow={() => newJoint({ name: "Liner", id: "", bottom: "" })}
                columns={[{ key: "name", label: "Name", width: 120 }, { key: "id", label: "ID in", type: "number", width: 70 }, { key: "bottom", label: "Bottom MD ft", type: "number", width: 90 }]} />
            </Block>
            <Block title="PORE / FORMATION PRESSURE POINTS">
              <RowsEditor testid="tally-pore" rows={pores} setRows={setPores} addLabel="Add pressure point"
                newRow={() => newJoint({ label: "", depth: "", pore_ppg: "", pore_psi: "" })}
                columns={[{ key: "label", label: "Zone", width: 100 }, { key: "depth", label: "TVD ft", type: "number", width: 80 }, { key: "pore_ppg", label: "Pore ppg", type: "number", width: 70 }, { key: "pore_psi", label: "or psi", type: "number", width: 70 }]} />
            </Block>
          </div>
          <Block title="RESULTS">
            <div className="flex flex-wrap gap-2 justify-end">
              <button data-testid="tally-pdf-btn" onClick={pdf} className="inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#94a3b8] px-3 py-2 mono text-xs uppercase tracking-widest transition-colors">
                <FileDown className="w-3.5 h-3.5" /> PDF
              </button>
              <SaveButton getPayload={payload} />
            </div>
            <TallyResults result={result} />
          </Block>
          {joints.length > 0 && <SummaryStrip s={result.summary} />}
        </div>
      </Section>
    </div>
  );
}
